import { z } from 'zod';
import { IntegrationType } from '../lib/integration';

/** The provider as the detail endpoint returns it; only what the form reads. */
export type ProviderDetail = {
  _id: string;
  name?: string;
  integration_type?: string;
  is_fulfillable?: boolean;
  is_warehouse?: boolean;
  is_oos_fulfillable?: boolean;
  is_auto_accept_fba_charges?: boolean;
  auto_upload?: boolean;
  label_paper_type?: string | null;
  quantity_type?: string;
  holdback_qty?: number | null;
  restock_limit?: number | string | null;
  identifier?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  contact_fax?: string | null;
  warehouse_codes?: string[];
  address?: {
    address_line_one?: string | null;
    address_line_two?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    country?: string | null;
  };
  marketplace_region_id?: { _id?: string } | null;
  shipping_policy?: {
    handling_time?: number | null;
    order_cutoff_time?: string | number | null;
  };
  buffers?: {
    inbound_buffer_days?: number | null;
    outbound_buffer_days?: number | null;
    inventory_buffer_stock_days?: number | null;
    /** Purchase-order days, not the velocity tiers below. */
    inventory_buffer_days?: number | null;
  };
  inventory_buffer_days?: { A?: number; B?: number; C?: number } | null;
  label_settings?: {
    exclude_carriers?: string[];
    use_label_name?: boolean;
    label_name?: string | null;
    statement_descriptor?: string | null;
  };
  tactical_credentials?: {
    tactical_3plnext_credentials?: unknown;
    tactical_legacy_credentials?: unknown;
  };
  restock_limit_url?: string;
};

export type ProviderDetailsValues = {
  name: string;
  is_fulfillable: boolean;
  is_warehouse: boolean;
  is_oos_fulfillable: boolean;
  is_auto_accept_fba_charges: boolean;
  auto_upload: boolean;
  label_paper_type: string;
  quantity_type: string;
  holdback_qty: number | null;
  handling_time: number | null;
  restock_limit: number | null;
  /** `HH:mm`, or empty for none. */
  order_cutoff_time: string;
  inbound_buffer_days: number | null;
  outbound_buffer_days: number | null;
  inventory_buffer_stock_days: number | null;
  inventory_buffer_days_A: number | null;
  inventory_buffer_days_B: number | null;
  inventory_buffer_days_C: number | null;
  address_line_one: string;
  address_line_two: string;
  city: string;
  state: string;
  zip_code: string;
  /** A marketplace region id. */
  country: string;
  identifier: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  contact_fax: string;
  exclude_carriers: string[];
  use_label_name: boolean;
  label_name: string;
  statement_descriptor: string;
  warehouse_codes: string[];
};

export const PAPER_TYPES = [
  'Plain Paper',
  'Blank Rectangle Label',
  'Thermal Printing Paper',
];

/** Velocity tiers for the minimum desired stock, from the fastest sellers. */
export const STOCK_TIERS = [
  {
    tier: 'A',
    detail:
      'Top 4% of gross profit out of total gross profit of all items receives classification of A',
  },
  {
    tier: 'B',
    detail:
      'Items making up 2-4% of gross profit out of all total gross profit receives classification of B',
  },
  { tier: 'C', detail: 'All other items receive classification of C' },
] as const;

const pad = (value: number) => String(value).padStart(2, '0');

function toTime(value: string | number | null | undefined): string {
  if (value == null || value === '') return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Quarter-hour choices; a saved time off the grid keeps its own entry. */
export function cutoffTimeOptions(current: string) {
  const times: string[] = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    times.push(`${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`);
  }
  if (current && !times.includes(current)) {
    times.push(current);
    times.sort();
  }
  return times.map(time => {
    const [hours, minutes] = time.split(':').map(Number);
    const hour12 = hours % 12 || 12;
    return {
      value: time,
      label: `${hour12}:${pad(minutes)} ${hours < 12 ? 'AM' : 'PM'}`,
    };
  });
}

export function toDetailsForm(provider: ProviderDetail): ProviderDetailsValues {
  const address = provider.address ?? {};
  const buffers = provider.buffers ?? {};
  const label = provider.label_settings ?? {};
  const tiers = provider.inventory_buffer_days ?? {};
  const restock = provider.restock_limit;

  return {
    name: provider.name ?? '',
    is_fulfillable: !!provider.is_fulfillable,
    is_warehouse: !!provider.is_warehouse,
    is_oos_fulfillable: provider.is_oos_fulfillable ?? false,
    is_auto_accept_fba_charges: !!provider.is_auto_accept_fba_charges,
    auto_upload: !!provider.auto_upload,
    label_paper_type: provider.label_paper_type ?? '',
    quantity_type: provider.quantity_type || 'unit',
    holdback_qty: provider.holdback_qty ?? null,
    handling_time: provider.shipping_policy?.handling_time ?? null,
    restock_limit:
      restock == null || restock === '' ? null : Number(restock) || null,
    order_cutoff_time: toTime(provider.shipping_policy?.order_cutoff_time),
    inbound_buffer_days: buffers.inbound_buffer_days ?? null,
    outbound_buffer_days: buffers.outbound_buffer_days ?? null,
    inventory_buffer_stock_days: buffers.inventory_buffer_stock_days ?? null,
    inventory_buffer_days_A: tiers.A || null,
    inventory_buffer_days_B: tiers.B || null,
    inventory_buffer_days_C: tiers.C || null,
    address_line_one: address.address_line_one ?? '',
    address_line_two: address.address_line_two ?? '',
    city: address.city ?? '',
    state: address.state ?? '',
    zip_code: address.zip_code ?? '',
    country: provider.marketplace_region_id?._id ?? '',
    identifier: provider.identifier ?? '',
    contact_name: provider.contact_name ?? '',
    contact_phone: provider.contact_phone ?? '',
    contact_email: provider.contact_email ?? '',
    contact_fax: provider.contact_fax ?? '',
    exclude_carriers: label.exclude_carriers ?? [],
    use_label_name: !!label.use_label_name,
    label_name: label.label_name ?? '',
    statement_descriptor: label.statement_descriptor ?? '',
    warehouse_codes: provider.warehouse_codes ?? [],
  };
}

/** Which parts of the form a provider's integration lets the user change. */
export function detailsAccess(
  integrationType: string | undefined,
  { isFBA, isAWD }: { isFBA: boolean; isAWD: boolean },
) {
  const isTactical = integrationType === IntegrationType.tactical3pl;
  const isFBM = integrationType === IntegrationType.fbam;

  return {
    isTactical,
    name: isAWD || isFBA || isTactical,
    detailsAndLocation: isAWD || isTactical || isFBM,
    fulfillment: isAWD || isFBM,
    warehouse: isFBM || isFBA,
    contact: isAWD || isFBM,
    shipmentStock: isFBM,
    labelSettings: isFBM,
    quantityAndPaper: isFBM || isFBA,
    /* These integrations decide the provider kind themselves. */
    allowsNoKind: isFBA || isAWD || isTactical,
    autoUpload: [
      IntegrationType.tactical3pl,
      IntegrationType.skupreme,
      IntegrationType.manual,
      IntegrationType.camelot,
    ].includes(integrationType ?? ''),
    quantityType: ![
      IntegrationType.awd,
      IntegrationType.deliverr,
      IntegrationType.manual,
      IntegrationType.spapi,
    ].includes(integrationType ?? ''),
    oosFulfillable: !['spapi', 'deliverr', 'walmart'].includes(
      integrationType ?? '',
    ),
  };
}

type SchemaOptions = { requiresName: boolean; dialCodeLength: number };

export function detailsSchema({ requiresName, dialCodeLength }: SchemaOptions) {
  return z.record(z.string(), z.unknown()).superRefine((values, ctx) => {
    const name = String(values.name ?? '').trim();
    if (requiresName && !name) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['name'],
        message: 'Name is required',
      });
    }

    const phone = String(values.contact_phone ?? '').trim();
    const message =
      phone && phone.length < 6 + dialCodeLength
        ? 'Phone Number must be atleast 6 digits'
        : phone.length > 12 + dialCodeLength
          ? 'Phone Number cannot be more than 12 digits'
          : undefined;
    if (message) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['contact_phone'],
        message,
      });
    }
  });
}

const orDefault = (value: unknown, fallback: number | null) =>
  value ? Number(value) : fallback;

const positive = (value: unknown) =>
  value && Number(value) > 0 ? Number(value) : 0;

function cutoffTimestamp(time: string | undefined, saved: unknown) {
  if (!time) return null;
  const [hours, minutes] = time.split(':').map(Number);
  const base =
    saved == null || saved === '' ? new Date() : new Date(saved as string);
  const date = Number.isNaN(base.getTime()) ? new Date() : base;
  date.setHours(hours, minutes, 0, 0);
  return String(date.getTime());
}

/**
 * The update body. Hidden fields are unregistered, so `values` holds only what
 * is on screen; the numeric settings still go out with their defaults, as they
 * always have.
 */
export function toDetailsPayload(
  values: Partial<ProviderDetailsValues>,
  provider: ProviderDetail,
) {
  const {
    exclude_carriers,
    use_label_name,
    label_name,
    statement_descriptor,
    inventory_buffer_days_A,
    inventory_buffer_days_B,
    inventory_buffer_days_C,
    ...fields
  } = values;
  const isFulfillable = !!values.is_fulfillable;
  const phone = values.contact_phone?.trim();

  const tiers = {
    A: positive(inventory_buffer_days_A),
    B: positive(inventory_buffer_days_B),
    C: positive(inventory_buffer_days_C),
  };
  const minimumStock = orDefault(values.inventory_buffer_stock_days, 1);

  return {
    ...fields,
    name: values.name?.trim() || provider.name,
    is_fulfillable: isFulfillable,
    integration_type: provider.integration_type,
    is_oos_fulfillable: values.is_oos_fulfillable ?? false,
    holdback_qty: orDefault(values.holdback_qty, 0),
    handling_time: orDefault(values.handling_time, 1),
    inbound_buffer_days: orDefault(values.inbound_buffer_days, 1),
    outbound_buffer_days: orDefault(values.outbound_buffer_days, 1),
    inventory_buffer_stock_days: isFulfillable
      ? values.is_warehouse
        ? { ...tiers, default: minimumStock }
        : tiers
      : minimumStock,
    order_cutoff_time: cutoffTimestamp(
      values.order_cutoff_time,
      provider.shipping_policy?.order_cutoff_time,
    ),
    restock_limit:
      values.restock_limit == null ? undefined : String(values.restock_limit),
    contact_phone: phone && !phone.includes('+') ? `+${phone}` : phone,
    country: values.country || provider.marketplace_region_id?._id,
    label_settings: {
      use_label_name: !!use_label_name,
      label_name: use_label_name ? label_name : undefined,
      statement_descriptor,
      exclude_carriers: exclude_carriers ?? [],
    },
  };
}
